<template>
  <div class="card">
    <!-- Search bar -->
    <div v-if="hasFilterableColumns" class="card-header">
      <input 
        v-model="searchTerm" 
        type="text" 
        class="form-control" 
        placeholder="Filtra..."
      >
    </div>

    <div class="card-body p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th 
                v-for="col in options.structure" 
                :key="col.key" 
                @click="col.sortable && sortBy(col.key)"
                :class="{ 'sortable': col.sortable, 'text-end': col.align === 'right', 'text-center': col.align === 'center' }"
              >
                {{ col.label }}
                <i 
                  v-if="col.sortable" 
                  :class="getSortIconClass(col.key)" 
                  class="ms-1"
                ></i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in filteredAndSortedData" :key="i">
              <td 
                v-for="col in options.structure" 
                :key="col.key"
                :class="{ 'text-end': col.align === 'right', 'text-center': col.align === 'center' }"
              >
                <slot :name="'field-' + col.key" :row="row">
                  {{ row[col.key] }}
                </slot>
              </td>
            </tr>
            <tr v-if="filteredAndSortedData.length === 0">
              <td :colspan="options.structure.length" class="text-center text-muted">
                Nessun dato trovato
              </td>
            </tr>
          </tbody>
          <tfoot v-if="$slots.footer">
            <slot name="footer"></slot>
          </tfoot>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="options.pagination" class="card-footer">
        <div class="d-flex justify-content-center align-items-center gap-3">
          <button 
            class="btn btn-sm btn-outline-primary" 
            :disabled="page_number === 1" 
            @click="page_number--"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span>Pagina {{ page_number }} / {{ totalPages }}</span>
          <button 
            class="btn btn-sm btn-outline-primary"
            @click="page_number++"
            :disabled="filteredAndSortedData.length < (options.pagination.page_size || 10)"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  options: {
    type: Object,
    default: () => ({
      structure: []
    })
  }
})

const searchTerm = ref('')
const sortKey = ref(null)
const sortOrder = ref('asc')
const page_number = ref(1)

const totalPages = computed(() => {
  if (!props.options.pagination) return 1
  const page_size = props.options.pagination.page_size || 10
  return Math.ceil(props.data.length / page_size)
})

// Calcola se ci sono colonne filtrabili
const hasFilterableColumns = computed(() =>
  props.options.structure.some(c => c.filterable)
)

// Funzione per ordinamento
function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function getSortIconClass(key) {
  if (sortKey.value !== key) return 'fas fa-sort text-muted'
  return sortOrder.value === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
}

// Computed per filtrare e ordinare i dati
const filteredAndSortedData = computed(() => {
  let result = [...props.data]

  // Filtro (su tutte le colonne filtrabili)
  if (searchTerm.value.trim()) {
    const term = searchTerm.value.toLowerCase()
    const filterCols = props.options.structure
      .filter(c => c.filterable)
      .map(c => c.key)

    result = result.filter(row =>
      filterCols.some(key => String(row[key] ?? '').toLowerCase().includes(term))
    )
  }

  // Ordinamento
  if (sortKey.value) {
    result.sort((a, b) => {
      let aVal = a[sortKey.value]
      let bVal = b[sortKey.value]
      
      // Handle null/undefined
      if (aVal == null) return 1
      if (bVal == null) return -1
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder.value === 'asc' ? aVal - bVal : bVal - aVal
      }
      
      // String comparison
      const res = String(aVal).localeCompare(String(bVal))
      return sortOrder.value === 'asc' ? res : -res
    })
  }

  // Paginazione
  if (props.options.pagination) {
    const page_size = props.options.pagination.page_size || 10
    const start = (page_number.value - 1) * page_size
    const end = start + page_size
    result = result.slice(start, end)
  }

  return result
})
</script>

<style scoped>
th.sortable {
  cursor: pointer;
  user-select: none;
}

th.sortable:hover {
  background-color: var(--bs-table-hover-bg, #f8f9fa);
}

.gap-3 {
  gap: 1rem;
}
</style>